import { DetailsTabSuccessMessages } from 'pages/invoices/InvoicePage';
import { useIsMultiApprovalFlow } from 'pages/invoices/layout/views/details-tab/invoice-matters/useCaseApproval';
import { caseAlertsNeedSummary, getInvoiceTabAlerts, InvoiceTabId } from 'pages/invoices/util/invoiceTabAlerts';
import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { APIInvoiceDetailsSlice, useGetInvoiceMatterSummaryQuery } from 'store/api/invoice-details';
import { InvoiceData } from 'types/invoices';
import { useInvoiceDetails } from './useInvoiceDetails';
import { useInvoiceFlow } from './useInvoiceFlow';

// The state the summary's cache selector reads, typed by the slice itself, as the tour reads the user's cache entry.
type SummaryCacheState = Parameters<
    ReturnType<typeof APIInvoiceDetailsSlice.endpoints.getInvoiceMatterSummary.select>
>[0];

function CaseSummary({ invoiceId, matterId }: { invoiceId: number; matterId: number }) {
    useGetInvoiceMatterSummaryQuery({ invoiceId, matterId });
    return null;
}

/**
 * Keeps loaded, while the page is open, the summaries of the cases whose state lets them raise an alert: the tabs'
 * marks count what those summaries say. A case's tab loads its summary anyway.
 */
export function InvoiceCaseSummaries({ invoice, isInViewMode }: { invoice: InvoiceData; isInViewMode: boolean }) {
    const { isDetailsReadOnly } = useInvoiceDetails(invoice);
    const { isRequestedToPostpone } = useInvoiceFlow(invoice);
    const isMultiApprovalFlow = useIsMultiApprovalFlow(invoice);
    const input = {
        invoice,
        isDetailsReadOnly: isInViewMode || isDetailsReadOnly,
        isMultiApprovalFlow,
        isRequestedToPostpone,
    };
    return (invoice.invoiceMatters ?? [])
        .filter((invoiceMatter) => caseAlertsNeedSummary(invoiceMatter, input))
        .map((invoiceMatter) => (
            <CaseSummary key={invoiceMatter.matterId} invoiceId={invoice.id} matterId={invoiceMatter.matterId} />
        ));
}

interface Args {
    invoice: InvoiceData;
    isInViewMode: boolean;
    successMessages: DetailsTabSuccessMessages;
    editingTab: InvoiceTabId | null;
}

/**
 * The page's alerts on every tab, from the invoice and the summaries of its cases that have loaded (see
 * {@link InvoiceCaseSummaries}): the order each tab leads with, and the counts of the tabs not on screen.
 */
export const useInvoiceTabAlerts = ({ invoice, isInViewMode, successMessages, editingTab }: Args) => {
    const {
        isDetailsReadOnly,
        hasUninvoicedTimecards,
        hasUninvoicedExpenses,
        isTimecardEmployeeMissingRole,
        isTimecardMissingHourlyRate,
    } = useInvoiceDetails(invoice);
    const {
        isRequestedToPostpone,
        isPostponeDisagreedContinued,
        isPostponeRejected,
        isInvoiceRejected,
        isPostponeConfirmed,
    } = useInvoiceFlow(invoice);
    const isMultiApprovalFlow = useIsMultiApprovalFlow(invoice);
    // One cache selector per case, built once: `select` makes a new one on every call, and this runs on every dispatch.
    const summarySelectors = useMemo(
        () =>
            (invoice.invoiceMatters ?? []).map(({ matterId }) =>
                APIInvoiceDetailsSlice.endpoints.getInvoiceMatterSummary.select({ invoiceId: invoice.id, matterId })
            ),
        [invoice.id, invoice.invoiceMatters]
    );
    const summaries = useSelector(
        (state: SummaryCacheState) => summarySelectors.map((select) => select(state).data),
        shallowEqual
    );

    return useMemo(
        () =>
            getInvoiceTabAlerts({
                invoice,
                isDetailsReadOnly: isInViewMode || isDetailsReadOnly,
                hasUninvoicedTimecards,
                hasUninvoicedExpenses,
                isTimecardEmployeeMissingRole,
                isTimecardMissingHourlyRate,
                successMessages,
                caseSummaries: Object.fromEntries(
                    (invoice.invoiceMatters ?? []).map(({ matterId }, index) => [matterId, summaries[index]])
                ),
                isMultiApprovalFlow,
                isRequestedToPostpone,
                flow: { isPostponeDisagreedContinued, isPostponeRejected, isInvoiceRejected, isPostponeConfirmed },
                editingTab,
            }),
        [
            invoice,
            isInViewMode,
            isDetailsReadOnly,
            hasUninvoicedTimecards,
            hasUninvoicedExpenses,
            isTimecardEmployeeMissingRole,
            isTimecardMissingHourlyRate,
            successMessages,
            summaries,
            isMultiApprovalFlow,
            isRequestedToPostpone,
            isPostponeDisagreedContinued,
            isPostponeRejected,
            isInvoiceRejected,
            isPostponeConfirmed,
            editingTab,
        ]
    );
};
